import {Localize} from "@dp-instant/i18n";
import {visibilityEffect} from "@dp-instant/ui";
import {UIConstants} from "@game/ui/constants";
import {apiClient} from "@game/api/ApiClient";
import {playUIClickSound} from "@game/helpers";
import {Images} from "@game/ui/images";
import {TransactionHistoryItem} from "../../../packages/shared/src/types/shop";
import cn from "classnames";
import {useState, useEffect, useRef} from "preact/hooks";
import "./TransactionHistoryPopup.less";

type TransactionHistoryPopupProps = {
    onClose: () => void;
};

type FilterType = "all" | "purchases" | "topups";
const PAGE_SIZE = 30;

function formatDate(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${(d.getFullYear() % 100).toString().padStart(2, "0")} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function shortenHash(hash: string): string {
    if (hash.length <= 16) return hash;
    return hash.slice(0, 8) + "..." + hash.slice(-6);
}

export const TransactionHistoryPopup = ({onClose}: TransactionHistoryPopupProps) => {
    const [invisible] = visibilityEffect(UIConstants.popup.startDelay);
    const [filter, setFilter] = useState<FilterType>("all");
    const [transactions, setTransactions] = useState<TransactionHistoryItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const listRef = useRef<HTMLDivElement>(null);

    const loadTransactions = async (filterType: FilterType, offset = 0, append = false) => {
        setLoading(true);
        try {
            const resp = await apiClient.getTransactions(PAGE_SIZE, offset, filterType);
            setTransactions(append ? (prev) => [...prev, ...resp.transactions] : resp.transactions);
            setTotal(resp.total);
        } catch {}
        setLoading(false);
    };

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
        loadTransactions(filter);
    }, [filter]);

    const handleFilterChange = (f: FilterType) => {
        playUIClickSound();
        setFilter(f);
    };

    const handleLoadMore = () => {
        playUIClickSound();
        loadTransactions(filter, transactions.length, true);
    };

    const hasMore = transactions.length < total;

    return (
        <div className="popups">
            <div className={cn("popup-txhistory", {invisible})}>
                <div className="popup-txhistory__handle" />
                <div className="popup-txhistory__header">
                    <div className="popup-txhistory__title">
                        {Localize.get("ui-tx-history-title", "Transaction History")}
                    </div>
                    <button className="popup-txhistory__close" onClick={() => { playUIClickSound(); onClose(); }}>
                        ✕
                    </button>
                </div>

                <div className="popup-txhistory__filters">
                    {(["all", "purchases", "topups"] as FilterType[]).map((f) => (
                        <button
                            key={f}
                            className={cn("popup-txhistory__filter", { active: filter === f })}
                            onClick={() => handleFilterChange(f)}
                        >
                            {f === "all" ? Localize.get("ui-tx-filter-all", "All")
                                : f === "purchases" ? Localize.get("ui-tx-filter-purchases", "Purchases")
                                : Localize.get("ui-tx-filter-topups", "Top-ups")}
                        </button>
                    ))}
                </div>

                <div className="popup-txhistory__list" ref={listRef}>
                    {transactions.map((tx) => {
                        const isCredit = tx.isCredit;
                        // For topups: show what was gained (+stars)
                        // For purchases: show what was spent
                        //   - TON purchases: show -TON amount
                        //   - Stars purchases: show -Stars amount
                        const isTon = tx.provider === "ton";
                        let amountText: string;
                        if (isCredit) {
                            amountText = `+${Math.abs(tx.amount)} ⭐`;
                        } else if (isTon && tx.tonAmount) {
                            amountText = `-${tx.tonAmount} TON`;
                        } else {
                            amountText = `-${Math.abs(tx.amount)}${tx.currency === "hard" ? " ⭐" : " 🪙"}`;
                        }
                        return (
                            <div className="popup-txhistory__item" key={tx.id}>
                                <div className="popup-txhistory__item-top">
                                    <span className="popup-txhistory__item-name">
                                        {tx.itemTitle || tx.item || tx.type}
                                    </span>
                                    <div className="popup-txhistory__item-amounts">
                                        <span className={cn("popup-txhistory__item-amount", {
                                            positive: isCredit,
                                            negative: !isCredit,
                                        })}>
                                            {amountText}
                                        </span>
                                        {isCredit && isTon && tx.tonAmount && (
                                            <span className="popup-txhistory__item-ton">~{tx.tonAmount} TON</span>
                                        )}
                                    </div>
                                </div>
                                <div className="popup-txhistory__item-bottom">
                                    <span className="popup-txhistory__item-date">{formatDate(tx.createdAt)}</span>
                                    <span className="popup-txhistory__item-id">#{tx.id}</span>
                                    {tx.tonTxHash && (
                                        <a
                                            className="popup-txhistory__item-txhash"
                                            href={`https://tonscan.org/tx/${tx.tonTxHash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {shortenHash(tx.tonTxHash)}
                                        </a>
                                    )}
                                    {tx.provider === "tribute" && (
                                        <span
                                            className="popup-txhistory__item-tribute-id"
                                            onClick={tx.tributePurchaseId ? () => {
                                                navigator.clipboard?.writeText(String(tx.tributePurchaseId));
                                                const tg = (window as any).Telegram?.WebApp;
                                                try { tg?.showAlert?.("Copied!"); } catch {}
                                            } : undefined}
                                        >
                                            {tx.tributePurchaseId ? `Tribute #${tx.tributePurchaseId} 📋` : "Tribute"}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {loading && <div className="popup-txhistory__loading">...</div>}

                    {!loading && transactions.length === 0 && (
                        <div className="popup-txhistory__empty">
                            {Localize.get("ui-tx-empty", "No transactions yet")}
                        </div>
                    )}

                    {!loading && hasMore && (
                        <button className="popup-txhistory__load-more" onClick={handleLoadMore}>
                            {Localize.get("ui-tx-load-more", "Load more")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
